<template>
  <div>
    <h2 class="box__title">{{ $t('notificationSettings.title') }}</h2>
    <p class="notification-settings__intro">
      {{ $t('notificationSettings.intro') }}
    </p>

    <!-- Origem Flow: por tipo × canal. O sininho é sempre entregue; aqui a
         pessoa escolhe onde mais quer ser avisada (push no celular/desktop
         e e-mail). -->
    <div class="notification-settings__table-wrap">
      <table class="notification-settings__table">
        <thead>
          <tr>
            <th>{{ $t('notificationSettings.typeHeader') }}</th>
            <th class="notification-settings__channel">
              <i class="iconoir-bell"></i>
              {{ $t('notificationSettings.channelApp') }}
            </th>
            <th class="notification-settings__channel">
              <i class="iconoir-smartphone-device"></i>
              {{ $t('notificationSettings.channelPush') }}
            </th>
            <th class="notification-settings__channel">
              <i class="iconoir-mail"></i>
              {{ $t('notificationSettings.channelEmail') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="type in types" :key="type.key">
            <td>
              <div class="notification-settings__type-name">
                {{ $t(`notificationSettings.types.${type.key}`) }}
              </div>
              <div class="notification-settings__type-help">
                {{ $t(`notificationSettings.typesHelp.${type.key}`) }}
              </div>
            </td>
            <td class="notification-settings__channel">
              <Checkbox :model-value="true" disabled></Checkbox>
            </td>
            <td class="notification-settings__channel">
              <Checkbox
                :model-value="isEnabled(type.key, 'push')"
                @update:model-value="toggle(type.key, 'push', $event)"
              ></Checkbox>
            </td>
            <td class="notification-settings__channel">
              <Checkbox
                :model-value="isEnabled(type.key, 'email')"
                @update:model-value="toggle(type.key, 'email', $event)"
              ></Checkbox>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <form class="notification-settings__frequency" @submit.prevent="save">
      <FormGroup
        :label="$t('emailNotifications.label')"
        :help-text="$t('emailNotifications.description')"
        required
      >
        <RadioGroup
          v-model="values.email_notification_frequency"
          :options="emailNotificationOptions"
          vertical-layout
        >
        </RadioGroup>
      </FormGroup>

      <div class="actions actions--right">
        <Button
          type="primary"
          size="large"
          :loading="loading"
          :disabled="!dirty || loading"
        >
          {{ $t('notificationSettings.save') }}
        </Button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { EMAIL_NOTIFICATIONS_FREQUENCY_OPTIONS } from '@baserow/modules/core/enums'
import { notifyIf } from '@baserow/modules/core/utils/error'

// Tipos que a pessoa pode ajustar. Os demais (convites, avisos do sistema)
// seguem sempre ligados.
export const NOTIFICATION_PREFERENCE_TYPES = [
  { key: 'row_comment' },
  { key: 'row_comment_mention' },
  { key: 'user_mention_in_rich_text_field' },
  { key: 'collaborator_added_to_row' },
  { key: 'form_submitted' },
]

export default {
  name: 'NotificationSettings',
  data() {
    return {
      loading: false,
      types: NOTIFICATION_PREFERENCE_TYPES,
      values: {
        email_notification_frequency:
          EMAIL_NOTIFICATIONS_FREQUENCY_OPTIONS.INSTANT,
        notification_preferences: {},
      },
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/getUserObject',
    }),
    emailNotificationOptions() {
      return [
        {
          label: this.$t('emailNotifications.instant'),
          value: EMAIL_NOTIFICATIONS_FREQUENCY_OPTIONS.INSTANT,
        },
        {
          label: this.$t('emailNotifications.daily'),
          value: EMAIL_NOTIFICATIONS_FREQUENCY_OPTIONS.DAILY,
        },
        {
          label: this.$t('emailNotifications.weekly'),
          value: EMAIL_NOTIFICATIONS_FREQUENCY_OPTIONS.WEEKLY,
        },
        {
          label: this.$t('emailNotifications.never'),
          value: EMAIL_NOTIFICATIONS_FREQUENCY_OPTIONS.NEVER,
        },
      ]
    },
    dirty() {
      return (
        this.values.email_notification_frequency !==
          this.user.email_notification_frequency ||
        JSON.stringify(this.values.notification_preferences) !==
          JSON.stringify(this.user.notification_preferences || {})
      )
    },
  },
  mounted() {
    this.setInitialValue()
  },
  methods: {
    setInitialValue() {
      this.values.email_notification_frequency =
        this.user.email_notification_frequency ||
        EMAIL_NOTIFICATIONS_FREQUENCY_OPTIONS.INSTANT
      this.values.notification_preferences = JSON.parse(
        JSON.stringify(this.user.notification_preferences || {})
      )
    },
    isEnabled(type, channel) {
      const entry = this.values.notification_preferences[type]
      if (!entry || entry[channel] === undefined) {
        return true
      }
      return !!entry[channel]
    },
    toggle(type, channel, value) {
      const preferences = { ...this.values.notification_preferences }
      const entry = { ...(preferences[type] || {}) }
      if (value) {
        delete entry[channel]
      } else {
        entry[channel] = false
      }
      if (Object.keys(entry).length === 0) {
        delete preferences[type]
      } else {
        preferences[type] = entry
      }
      this.values.notification_preferences = preferences
    },
    async save() {
      this.loading = true
      try {
        await this.$store.dispatch('auth/update', {
          email_notification_frequency:
            this.values.email_notification_frequency,
          notification_preferences: this.values.notification_preferences,
        })
        this.$store.dispatch('toast/info', {
          title: this.$t('notificationSettings.savedTitle'),
          message: this.$t('notificationSettings.saved'),
        })
      } catch (error) {
        notifyIf(error, 'settings.')
        this.setInitialValue()
      }
      this.loading = false
    },
  },
}
</script>
